import { useState, useEffect } from 'react';
import Postmonger from 'postmonger';
import dataSourceInfoMock from './mocks/postmonger/dataSourceInfo-mock';
import initActivityMock from './mocks/postmonger/initActivity-mock';
import journeyInfoMock from './mocks/postmonger/journeyInfo-mock';
import { createCustomAlert } from './utils/utils';
import AppContainer from './containers/AppContainer';
import './sass/main.scss';

const connection = new Postmonger.Session();

function App() {
  // ACTIVITY CONFIGURATION INHERENT TO POSTMONGER
  const [activityConfig, setActivityConfig] = useState({});

  // BOOL TO SET WHEN THE CAMPAIGN HAS BEEN CONFIGURED AND SAVED. CLOSES THE ACTIVITY WHEN SET TO TRUE
  const [saved, setSaved] = useState(false);

  // APP CONFIGURATION
  const [appConfig, setAppConfig] = useState({
    idPushTTL: '',
    idJourney: '',
    copiedKeyJourney: '',
    eventDefinitionKey: '',
    version: 0
  });

  // INIT
  useEffect(() => {

    if (!STANDALONE) {

      connection.trigger('ready');
      connection.on('initActivity', onInitActivity);

      connection.trigger('requestTriggerEventDefinition');
      connection.on('requestedTriggerEventDefinition', getDataSourceInfo);

      connection.trigger('requestInteraction');
      connection.on('requestedInteraction', getJourneyInfo);

    } else {
      // ====== MOCK ======
      onInitActivity(initActivityMock);
      getDataSourceInfo(dataSourceInfoMock);
      getJourneyInfo(journeyInfoMock);
      // ====== END MOCK ======
    }

  }, []);

  // SAVE THE NEW ACTIVITY CONFIGURATION AND CLOSE IT
  useEffect(() => {
    if (saved) {
      // console.warn('update activity: ', activityConfig);
      connection.trigger('updateActivity', activityConfig);
    }
  }, [activityConfig, saved])

    // SET STARTING ACTIVITY CONFIGURATION AND APP PARAMETERS
    // (WHEN THE ACTIVITY IS COPIED WE NEED TO USE THE PREVIOUS KEY TO RETRIEVE THE OLD TEMPLATE)
  function onInitActivity(activityInfo) {
    // console.warn('init activity: ', activityInfo);

    if (!STANDALONE) connection.trigger('updateButton', { button: 'next', enabled: false });
    // null safe
    if (!activityInfo.arguments) activityInfo.arguments = {};
    if (!activityInfo.arguments.execute) activityInfo.arguments.execute = {};

    const { inArguments = [] } = activityInfo.arguments.execute;

    const { key } = inArguments.find(arg => arg.key) || { key: {} };

    setActivityConfig(activityInfo);
    setAppConfig(prev => ({
      ...prev,
      idPushTTL: activityInfo.key,
      copiedKeyJourney: key
    }))
  };

  // SET APP PARAMETER AND CHECK IF THE JOURNEY HAS BEEN SAVED
  function getJourneyInfo(journeyInfo) {
    // console.warn('journey info: ', journeyInfo);
    if (journeyInfo.id) {
      setAppConfig((prev) => ({
        ...prev,
        idJourney: journeyInfo.id,
        version: journeyInfo.version
      }));
    } else {
      createCustomAlert('ATTENZIONE!','Per procedere chiudi la modale e SALVA il Journey!');
    }
  }

  // SET APP PARAMETER AND CHECK IF THE ACTIVITY IS CORRECTLY CONFIGURED
  function getDataSourceInfo(dataSourceInfo) {
    // console.warn('datasource info: ', dataSourceInfo);
    if (dataSourceInfo.type && dataSourceInfo.type !== 'ContactEvent') {
      setAppConfig((prev) => ({
        ...prev,
        eventDefinitionKey: dataSourceInfo.eventDefinitionKey
      }));
    } else {
      createCustomAlert('ATTENZIONE', 'Prima di procedere con la creazione della campagna è necessario chiudere questa modale, impostare una Data Extension e salvare il Journey. ')
    }
  }

  // UPDATE THE ACTIVITY CONFIGURATION WITH THE APP PARAMETERS BEFORE CLOSING IT
  function updateActivityConfig(payload) {
    const { idPushTTL, idJourney, eventDefinitionKey, version } = payload;

    setActivityConfig(prev => {
      const newActivityConfig = Object.assign({}, prev);
      // null safe
      if (!newActivityConfig.arguments) newActivityConfig.arguments = {};
      if (!newActivityConfig.arguments.execute) newActivityConfig.arguments.execute = {};

      let { inArguments = [] } = newActivityConfig.arguments.execute;

      const newKey = { key: { idPushTTL, idJourney, version } };

      const newAnalyticsInfo = {
        analyticsInfo: {
          messageNM: '{{Event.' + eventDefinitionKey + '.MESSAGE_NM}}',
          coCampagna: '{{Event.' + eventDefinitionKey + '.CO_CAMPAGNA}}',
          idTitolare: '{{Event.' + eventDefinitionKey + '.ID_TITOLARE_NEXI}}',
          idIam: '{{Event.' + eventDefinitionKey + '.ID_IAM}}',
          dtProcessed: '{{Event.' + eventDefinitionKey + '.DT_PROCESSED}}',
          panAlias: '{{Event.' + eventDefinitionKey + '.CO_CARTA_ALIAS}}',
          idCampaign: '{{Event.' + eventDefinitionKey + '.ID_CAMPAGNA}}'
        }
      }

      const newDateParamsInfo = {
        dateInfo: {
          expiringCampaignDate: '{{Event.' + eventDefinitionKey + '.EXPIRING_CAMPAIGN}}',
          persistenceNotificationDate: '{{Event.' + eventDefinitionKey + '.PERSISTENCE_NOTIFICATION}}'
        }
      }

      const newNavigationInfo = {
        navigationInfo: {
          targetPanAlias: '{{Event.' + eventDefinitionKey + '.CO_CARTA_ALIAS_TARGET}}'
        }
      }

      const newDynamicParamsInfo = {
        dynamicParams: {
          PAR1: '{{Event.' + eventDefinitionKey + '.PAR1}}',
          PAR2: '{{Event.' + eventDefinitionKey + '.PAR2}}',
          PAR3: '{{Event.' + eventDefinitionKey + '.PAR3}}',
          PAR4: '{{Event.' + eventDefinitionKey + '.PAR4}}',
          PAR5: '{{Event.' + eventDefinitionKey + '.PAR5}}',
          PAR6: '{{Event.' + eventDefinitionKey + '.PAR6}}',
          PAR7: '{{Event.' + eventDefinitionKey + '.PAR7}}',
          PAR8: '{{Event.' + eventDefinitionKey + '.PAR8}}',
          PAR9: '{{Event.' + eventDefinitionKey + '.PAR9}}',
          PAR10: '{{Event.' + eventDefinitionKey + '.PAR10}}'
        }
      }

      const newQueryParamsInfo = {
        queryParams: {
          sfmc_p1: '{{Event.' + eventDefinitionKey + '.sfmc_p1}}',
          sfmc_p2: '{{Event.' + eventDefinitionKey + '.sfmc_p2}}',
          sfmc_p3: '{{Event.' + eventDefinitionKey + '.sfmc_p3}}',
          sfmc_p4: '{{Event.' + eventDefinitionKey + '.sfmc_p4}}',
          sfmc_p5: '{{Event.' + eventDefinitionKey + '.sfmc_p5}}'
        }
      }

      // inArguments is an array of objects
      inArguments = inArguments.map(inArg => {
        if (inArg.key) return newKey;
        if (inArg.analyticsInfo) return newAnalyticsInfo;
        if (inArg.dateInfo) return newDateParamsInfo;
        if (inArg.navigationInfo) return newNavigationInfo;
        if (inArg.dynamicParams) return newDynamicParamsInfo;
        if (inArg.queryParams) return newQueryParamsInfo;
        return inArg;
      });

      // adds the new arguments if they were not present when mapping the array
      if (!inArguments.some(inArg => inArg.key)) inArguments = inArguments.concat(newKey);
      if (!inArguments.some(inArg => inArg.analyticsInfo)) inArguments = inArguments.concat(newAnalyticsInfo);
      if (!inArguments.some(inArg => inArg.dateInfo)) inArguments = inArguments.concat(newDateParamsInfo);
      if (!inArguments.some(inArg => inArg.navigationInfo)) inArguments = inArguments.concat(newNavigationInfo);
      if (!inArguments.some(inArg => inArg.dynamicParams)) inArguments = inArguments.concat(newDynamicParamsInfo);
      if (!inArguments.some(inArg => inArg.queryParams)) inArguments = inArguments.concat(newQueryParamsInfo);

      newActivityConfig.arguments.execute.inArguments = inArguments;

      newActivityConfig.metaData = newActivityConfig.metaData
      ? { ...newActivityConfig.metaData, isConfigured: true }
      : { isConfigured: true }
      return newActivityConfig;
    })

    // triggers the useEffect on line: 49, closing the activity
    setSaved(true);
  }

  return (
    <div className="app">
      {appConfig.idPushTTL ? (
        <AppContainer
          {...appConfig}
          updateAppConfig={(idPushTTL) => setAppConfig(prev => ({ ...prev, idPushTTL }))}
          updateActivityConfig={() => updateActivityConfig(appConfig)}
          setAppConfig={setAppConfig}
        />
      ) : (
        <div className="courtesy-page">
          <img src="images/sessioneScaduta.png" alt=""/>
          <p>Errore di connessione!</p>
          <p>Chiudi e riapri la custom activity oppure riprova più tardi.</p>
        </div>
      )}
    </div>
  );
}

export default App;
